import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { cn } from '../../utils/cn'
import type { ProductPitch } from './model'

const TAG =
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-1.5 py-0 text-[10px] leading-4 font-medium tracking-wider whitespace-nowrap'

const CTA =
  'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md border-2 border-brand bg-brand px-4 text-sm font-semibold whitespace-nowrap text-ink-900 outline-none transition-all hover:border-brand-600 hover:bg-brand-600 focus-visible:ring-[3px] focus-visible:ring-brand-700/50'

const MUTED_CTA =
  'inline-flex h-6 shrink-0 items-center justify-center gap-1 rounded-md text-xs font-semibold whitespace-nowrap text-ink-600 outline-none transition-all hover:text-ink focus-visible:ring-[3px] focus-visible:ring-brand-700/50'

/** Cards for products the account does not hold. */
export function SidebarPitches({ pitches }: { pitches: ProductPitch[] }) {
  if (pitches.length === 0) return null

  return (
    <div className="flex flex-col gap-3 px-3 py-4 group-data-[collapsible=icon]:hidden">
      {pitches.map((pitch) => (
        <div
          key={pitch.label}
          className={cn(
            'flex flex-col items-start gap-2 rounded-lg border p-3',
            pitch.muted ? 'border-ink-50' : 'border-brand-200 bg-brand-50'
          )}
        >
          <div className="flex items-center gap-1.5">
            <span className="text-ink-900 text-sm font-medium">{pitch.label}</span>
            {pitch.tag && (
              <span className={cn(TAG, pitch.muted ? 'bg-ink-100 text-ink-900' : 'bg-brand-300 text-ink')}>
                {pitch.tag}
              </span>
            )}
          </div>
          <p className="text-ink-600 text-xs leading-relaxed">{pitch.pitch}</p>
          {pitch.href && (
            <PitchLink href={pitch.href} className={pitch.muted ? MUTED_CTA : CTA}>
              {pitch.cta}
            </PitchLink>
          )}
          {pitch.note && <p className="text-ink-500 text-[10px] leading-4">{pitch.note}</p>}
        </div>
      ))}
    </div>
  )
}

/** Same tab either way: another app's page loads in full, a route of this one goes through the router. */
function PitchLink({ href, className, children }: { href: string; className: string; children: ReactNode }) {
  if (href.startsWith('http')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    )
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  )
}
